import { AlertTriangle, Check, Loader2, Sparkles, X } from "lucide-react";
import { RESOURCE_DESCRIPTION_MAX } from "@/lib/agent/bulkInfoLinks";
import type { ResourceDescriptionSource } from "@/types/agent/info-links";

interface ResourceDescriptionFieldProps {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  source?: ResourceDescriptionSource;
  /** The AI is reading this resource right now. */
  pending: boolean;
  /** The AI only saw the resource's name, not its content. */
  nameOnly?: boolean;
  /** The link didn't answer when it was opened. */
  unreachable?: boolean;
  suggestedLabel?: string | null;
  onAcceptLabel?: () => void;
  onDismissLabel?: () => void;
}

export const ResourceDescriptionField = ({
  value,
  onChange,
  placeholder,
  source,
  pending,
  nameOnly = false,
  unreachable = false,
  suggestedLabel,
  onAcceptLabel,
  onDismissLabel,
}: ResourceDescriptionFieldProps) => {
  const isAiWritten = source === "ai" && !!value.trim();

  return (
    <div className="mt-1.5 space-y-1.5">
      {pending && !value.trim() ? (
        <div className="flex items-center gap-2 rounded-md border border-dashed border-genie-primary/40 bg-genie-primary-light/60 px-2 py-2 text-xs text-genie-primary animate-pulse">
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
          Reading it and writing a description…
        </div>
      ) : (
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          maxLength={RESOURCE_DESCRIPTION_MAX}
          rows={2}
          className={`w-full resize-none rounded-md border bg-white px-2 py-1.5 text-base sm:text-xs text-gray-700 placeholder:text-gray-400 focus:outline-none focus:ring-1 focus:ring-genie-primary transition-colors ${isAiWritten ? "border-genie-primary/40" : "border-gray-200"}`}
        />
      )}

      {(isAiWritten || suggestedLabel || unreachable) && (
        <div className="flex flex-wrap items-center gap-1.5">
          {unreachable && (
            <span className="inline-flex items-center gap-1 rounded-full bg-genie-accent-light px-2 py-0.5 text-[11px] font-medium text-gray-700">
              <AlertTriangle className="w-3 h-3" />
              Couldn't open this link · check the URL
            </span>
          )}
          {isAiWritten && (
            <span className="inline-flex items-center gap-1 rounded-full bg-genie-primary-light px-2 py-0.5 text-[11px] font-medium text-genie-primary">
              <Sparkles className="w-3 h-3" />
              {nameOnly ? "AI guess from the name · please review" : "AI-written · review"}
            </span>
          )}
          {suggestedLabel && onAcceptLabel && onDismissLabel && (
            <span className="inline-flex max-w-full items-center gap-1 rounded-full bg-genie-secondary-light pl-2 text-[11px] text-gray-700">
              <span className="truncate">
                Rename to <span className="font-medium">“{suggestedLabel}”</span>?
              </span>
              <button
                type="button"
                onClick={onAcceptLabel}
                className="p-1.5 text-genie-secondary hover:text-genie-secondary-hover"
                aria-label={`Rename to ${suggestedLabel}`}
              >
                <Check className="w-3.5 h-3.5" />
              </button>
              <button
                type="button"
                onClick={onDismissLabel}
                className="p-1.5 pr-2 text-gray-400 hover:text-gray-600"
                aria-label="Keep the current name"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </span>
          )}
        </div>
      )}
    </div>
  );
};
